// Before anything that defines a zod schema (see the module).
import "@/lib/zod-config";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { router } from "@/router";
import { queryClient } from "@/lib/query-client";
import { trackNavigationHistory } from "@/lib/navigationHistory";
import { cachedUserId, restorePersistedShell } from "@/lib/persistence";
import { installGlobalErrorHandlers } from "@/lib/diagnostics";
import { activateDrafts } from "@/features/chat/draftPersistence";
import { watchSummaryChanges } from "@/features/worktree/snapshotCache";
import { installTheme } from "@/lib/theme";
// Registers the chat write-behind, which follows the persisted user.
import "@/features/chat/chatPersistence";
import "@/styles/global.css";
// Loaded after the sheet it refines, so equal-specificity rules win on order.

// Every uncaught error and rejection lands in "Copy diagnostics".
installGlobalErrorHandlers();
// The chosen theme is on the root before anything renders against it.
installTheme();
// Unsent drafts and sends come back before the first composer mounts: the
// last member's, for the organization in the address (CONTRACT C3). The org
// shell confirms the member once the session answers.
{
  const orgId = location.pathname.match(/^\/o\/([^/]+)/)?.[1];
  const userId = cachedUserId();
  if (orgId && userId) activateDrafts(userId, decodeURIComponent(orgId));
}

// Recording starts before the first render: the entry you loaded on is the one
// the header's back arrow has to know about, and `subscribe` only reports what
// happens after it is called.
trackNavigationHistory(router.history);
// Any summary change expires exactly the files and folders it changed.
watchSummaryChanges(queryClient);

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("#root element missing");
const root = rootEl;
createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);

// The last signed-in user's shell (orgs, repositories, tasks) comes off disk
// into memory while the session check runs. Nothing renders from it until the
// session names that same user (`AuthedLayout`); then the workspace paints
// from it and revalidates instead of drawing skeletons.
void restorePersistedShell(queryClient);
